<script lang="ts" setup>
import type { OptionsMap } from '~/types/elementsUi'
import type { RouteLocationRaw } from '#vue-router'
import { useScrollToElement } from '#imports'
import type { MaybeArray } from '~/types/utils'
import { hardcodedFocusMunicipalities } from '~/models/hardcodedArrays'
import type { TailwindColor } from '#shared/types/elementsUi'

interface LocationsSectionProps {
  title: string
  description: string
  locations?: OptionsMap[]
  pageExists?: boolean
  backgroundColor?: TailwindColor
}

const props = defineProps<LocationsSectionProps>()

const descriptionElement = useTemplateRef('description')
const { scrollToElement } = useScrollToElement(descriptionElement, {
  block: 'start'
})

const isActive = ref(false)

const { t, te, tm } = useI18n()

const viewport = useViewport()
const viewportIsLessThanTablet = computed(() => viewport.isLessThan('tablet'))

const threshold = computed(() => (viewportIsLessThanTablet.value ? 5 : 12))
const locations = computed(() => props.locations ?? hardcodedFocusMunicipalities)

const selectedLocations = computed<OptionsMap[]>(() => {
  if (isActive.value) {
    return locations.value
  } else {
    return locations.value.slice(0, threshold.value)
  }
})

const computedDescription = computed<MaybeArray<string>>(() => {
  const descriptionArray = tm(props.description)

  if (Array.isArray(descriptionArray)) {
    return descriptionArray
  } else if (te(props.description)) {
    return t(props.description)
  } else {
    return props.description
  }
})

const toggleLocations = () => {
  isActive.value = !isActive.value

  if (!isActive.value && viewportIsLessThanTablet.value) {
    scrollToElement()
  }
}
</script>

<template>
  <VSection :title :ui="{ section: backgroundColor && `bg-${backgroundColor}` }">
    <template #content>
      <div ref="description" class="my-6 prose max-w-section scroll-m-52">
        <template v-if="Array.isArray(computedDescription)">
          <p
            v-for="(paragraph, index) in computedDescription"
            :key="`location-paragraph-${index}`"
          >
            {{ paragraph }}
          </p>
        </template>
        <p v-else>
          {{ computedDescription }}
        </p>
      </div>

      <TransitionGroup name="location-item" tag="div" class="columns multiline">
        <div
          v-for="location in selectedLocations"
          :key="location.id"
          class="column one-quarter"
        >
          <VLink
            v-if="!location.disabled && pageExists"
            :to="{ path: location.id } as RouteLocationRaw"
            class="flex items-center text-black! no-underline dark:text-white!"
          >
            <VIcon class="mr-1" icon="fa-location-dot" />
            <span class="my-0! text-left underline">
              {{ location.name }}
            </span>
          </VLink>
          <div v-else class="flex items-center">
            <VIcon class="mr-1" icon="fa-location-dot" />
            <span class="my-0! text-left">
              {{ location.name }}
            </span>
          </div>
        </div>
      </TransitionGroup>

      <div v-if="locations.length > threshold" class="flex lg:justify-end">
        <VButton color="text" @click="toggleLocations">
          {{ t(`buttons.${isActive ? 'show-less' : 'show-more'}`) }}
        </VButton>
      </div>

      <slot name="after" />
    </template>
  </VSection>
</template>

<style scoped>
.location-item-enter-active,
.location-item-leave-active {
  transition: all 0.3s ease;
}

.location-item-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.location-item-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Ensure smooth layout transitions */
.location-item-move {
  transition: transform 0.3s ease;
}
</style>
